import React, { useEffect } from 'react';
import {
  LoginOutlined,
  BarChartOutlined,
  DollarOutlined,
  AppstoreOutlined,
  CameraOutlined,
  MailOutlined,
  FlagOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { ADMIN_ROLES } from '../../../constants/roles';
import { getDefaultRoute } from '../../../utils/getDefaultRoute';

interface PlatformArea {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const PLATFORM_AREAS: PlatformArea[] = [
  {
    icon: <BarChartOutlined className="text-xl" />,
    title: 'User Analytics',
    description:
      'View platform usage, growth trends, demographics, sports breakdowns, and athlete search activity.',
  },
  {
    icon: <DollarOutlined className="text-xl" />,
    title: 'Monetization Insights',
    description:
      'Review subscription revenue, sendout volume, and chat engagement metrics.',
  },
  {
    icon: <AppstoreOutlined className="text-xl" />,
    title: 'User & Program Data',
    description:
      'Search and inspect athlete, coach, scout, college, and athletic program records.',
  },
  {
    icon: <CameraOutlined className="text-xl" />,
    title: 'Media Review',
    description: 'Browse and inspect media assets uploaded across the platform.',
  },
  {
    icon: <MailOutlined className="text-xl" />,
    title: 'Reports & Exports',
    description: 'Download marketing exports and reference glossary terminology.',
  },
  {
    icon: <FlagOutlined className="text-xl" />,
    title: 'Platform Configuration',
    description:
      'Adjust feature flags, system settings, and other operational controls where enabled.',
  },
];

const Landing: React.FC = () => {
  const { isAuthenticated, isLoading, logout, login, user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignIn = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('logout') === 'true') {
      logout().then(() => {
        navigate('/', { replace: true });
      });
    }
  }, [location.search, logout, navigate]);

  useEffect(() => {
    if (isAuthenticated && !isLoading && user) {
      if (ADMIN_ROLES.some((r) => user.roles?.includes(r))) {
        navigate(getDefaultRoute(hasPermission), { replace: true });
      } else {
        navigate('/access-denied', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, user, navigate, hasPermission]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/20 border-t-white mx-auto mb-4" />
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10 px-6 lg:px-12 py-5">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between">
          <img
            src="/ScoutUsLight.png"
            alt="ScoutUs College"
            className="h-8 w-auto"
          />
          <div className="flex items-center gap-2 select-none" aria-label="Admin Portal">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" aria-hidden="true" />
            <span className="text-sm font-medium text-white/90 tracking-wide">
              Admin Portal
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-slate-900 text-white overflow-hidden min-h-[62vh] flex flex-col justify-center pt-20 lg:pt-24">
        <picture className="absolute inset-0 opacity-50">
          <source
            media="(max-width: 768px)"
            srcSet="/images/sports-banner-mobile.jpg"
          />
          <img
            src="/images/sports-banner-desktop.jpg"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-900/60 to-slate-900/75" />

        <div className="relative w-full max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 lg:pb-24">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <SafetyCertificateOutlined className="text-blue-400" />
              <span className="text-sm font-medium text-blue-300">
                Authorized users only
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold leading-tight mb-4">
              ScoutUs College
              <br />
              <span className="text-slate-300">Admin Portal</span>
            </h1>

            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-2xl">
              Internal dashboard for viewing platform analytics, inspecting user and
              program data, and monitoring ScoutUs College operations.
            </p>

            <button
              type="button"
              onClick={handleSignIn}
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-lg px-10 py-4 rounded-lg transition-colors duration-200 shadow-lg shadow-blue-900/30"
            >
              <LoginOutlined className="text-xl" />
              Sign In to Admin Portal
            </button>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="flex-1 bg-white px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-[1440px] mx-auto">
          <div className="mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-3">
              Analytics & platform data
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl">
              A read-focused view of ScoutUs College — metrics, records, and exports
              available to authorized users after signing in.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLATFORM_AREAS.map((area) => (
              <div
                key={area.title}
                className="group border border-slate-200 rounded-xl p-6 hover:border-slate-300 hover:shadow-md transition-all duration-200"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 mb-4 group-hover:bg-slate-900 group-hover:text-white transition-colors duration-200">
                  {area.icon}
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  {area.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white px-6 lg:px-12 py-10">
        <div className="max-w-[1440px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <img
            src="/ScoutUsLight.png"
            alt="ScoutUs College"
            className="h-7 w-auto opacity-80"
          />
          <p className="text-sm text-slate-400">
            © {new Date().getFullYear()} ScoutUs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
